import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sun, Moon, User, LogOut, LayoutDashboard, ShoppingBag } from 'lucide-react';
import SearchBar from '../products/SearchBar';
import { useAuth } from '../../hooks/useAuth';
import { useUiStore } from '../../store/uiStore';
import { useWishlistStore } from '../../store/wishlistStore';

const Header: React.FC = () => {
  const { user, isLoggedIn, logout } = useAuth();
  const { theme, toggleTheme, openAuthModal } = useUiStore();
  const wishlistCount = useWishlistStore((s) => s.items.length);

  return (
    <header className="sticky top-0 z-40 bg-nav shadow-md">
      <div className="container mx-auto px-4">
        {/* Top bar */}
        <div className="flex items-center gap-4 h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <ShoppingBag
              className="w-7 h-7 text-amber-400"
              style={{ filter: 'drop-shadow(0 0 8px rgba(245,158,11,0.5))' }}
            />
            <span className="logo-brand-text text-lg hidden sm:inline">Amazon Onlines</span>
          </Link>

          {/* Search */}
          <div className="flex-1">
            <SearchBar />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className="p-2 rounded-lg text-gray-200 hover:text-white hover:bg-white/10 transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Wishlist */}
            {isLoggedIn && (
              <Link
                to="/wishlist"
                className="relative p-2 rounded-lg text-gray-200 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Wishlist"
              >
                <Heart className="w-5 h-5" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>
            )}

            {/* Auth */}
            {isLoggedIn ? (
              <div className="relative group">
                <button className="flex items-center gap-2 text-gray-200 hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-white/10">
                  <User className="w-5 h-5" />
                  <span className="text-sm hidden md:block">{user?.firstName ?? 'Account'}</span>
                </button>
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-150 animate-slide-down">
                  <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <User className="w-4 h-4" /> My Profile
                  </Link>
                  <Link to="/wishlist" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <Heart className="w-4 h-4" /> Wishlist
                  </Link>
                  <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </Link>
                  <hr className="my-1 border-gray-100 dark:border-gray-700" />
                  <button
                    onClick={() => logout()}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full transition-colors"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => openAuthModal('login')}
                className="btn-primary py-2 px-4 text-sm"
              >
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Category nav */}
        <nav className="flex items-center gap-1 overflow-x-auto scrollbar-none pb-2 text-sm text-gray-300">
          {[
            { label: 'Electronics',   q: 'electronics' },
            { label: 'Clothing',      q: 'clothing shoes jewelry' },
            { label: 'Books',         q: 'books' },
            { label: 'Movies',        q: 'movies music games' },
            { label: 'Computers',     q: 'computers laptops' },
            { label: 'Smart Home',    q: 'smart home' },
            { label: 'Home & Garden', q: 'home garden tools' },
            { label: 'Pets',          q: 'pet supplies' },
            { label: 'Grocery',       q: 'food grocery' },
            { label: 'Beauty',        q: 'beauty health' },
            { label: 'Toys & Baby',   q: 'toys kids baby' },
            { label: 'Handmade',      q: 'handmade' },
            { label: 'Sports',        q: 'sports outdoors' },
            { label: 'Automotive',    q: 'automotive' },
            { label: 'Industrial',    q: 'industrial scientific' },
            { label: 'Whole Foods',   q: 'whole foods' },
          ].map(({ label, q }) => (
            <Link
              key={label}
              to={`/search?q=${encodeURIComponent(q)}`}
              className="shrink-0 px-3 py-1 rounded-md hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap"
            >
              {label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;
